import * as React from 'react';

import type { CampaignStateDto } from '@/api/campaign-client';

// =============================================================================
// campaign-cycle-navigator.tsx — Navigateur de cycle PERMANENT (DBU §2.5 l.1057-1059).
// =============================================================================
// Visuel TRANSVERSE de progression du cycle de campagne, destiné à être affiché EN PERMANENCE
// sur les pages de la zone campagne (parité DBU : Création › Lancement C1 › Validation › Cycle 2 ›
// Analyses › Publication Pulse › Archivage). Composant PUR : l'unique entrée est l'état de campagne
// (autorité API) ; aucune règle métier, aucune transition — il LIT l'état et le SITUE sur la frise.
//
// WCAG 2.1 AA (ST-NFR-03) : <nav aria-label> + <ol> ordonné ; chaque étape porte un LIBELLÉ TEXTE et
// un statut EN TEXTE (sr-only « étape franchie / en cours / à venir ») : la couleur/forme n'est qu'un
// renfort (jamais seule porteuse). L'étape courante est marquée aria-current="step". Le résumé visible
// « Étape n / 6 · … en cours » double la liste pour l'œil ; il est masqué aux lecteurs d'écran (pas de
// double annonce, la liste sr-only porte déjà l'information).

/** Une étape du cycle de campagne (frise DBU) et les états API qui la matérialisent. */
interface CycleStage {
  readonly id: string;
  readonly label: string;
  /** États API (parité Prisma CampaignState) rattachés à cette étape. */
  readonly states: readonly CampaignStateDto[];
}

/**
 * Les 6 étapes de la frise, dans l'ordre du cycle de vie. Le mapping état → étape est une table de
 * traduction PURE (aucune règle métier). `Abandonnee` est rattaché à « Archivage » (terminal
 * hors-parcours) et traité à part par `stageStatus` (les étapes amont ne sont PAS présentées comme franchies).
 *
 * CORRIGÉ le 2026-09-15 — relevé Gate Human. Deux défauts, une seule cause : la frise nommait des
 * ÉTAPES D'OUTIL là où l'utilisateur raisonne en CYCLES.
 *
 *   · « Validation » ne correspondait à AUCUNE phase réelle du processus. L'état `AnalyseC1` est
 *     l'analyse des données saisies PENDANT le cycle 1 : elle relève intégralement de ce cycle-là,
 *     et l'afficher comme une étape distincte faisait croire à une phase de validation qui n'existe
 *     pas. `AnalyseC1` rejoint donc « Cycle 1 ».
 *   · « Lancement C1 » nommait le geste d'ouverture, pas la période. L'étape s'appelle « Cycle 1 »,
 *     symétrique de « Cycle 2 ».
 *
 * `AnalyseC2` reste dans « Analyses » : c'est l'analyse qui SUIT la clôture du second cycle et qui
 * porte la négociation — elle n'appartient pas au cycle 2 comme `AnalyseC1` appartient au cycle 1.
 */
const CYCLE_STAGES: readonly CycleStage[] = [
  { id: 'creation', label: 'Création', states: ['Brouillon', 'Planifiee'] },
  { id: 'cycle-1', label: 'Cycle 1', states: ['OuverteC1', 'AnalyseC1'] },
  { id: 'cycle-2', label: 'Cycle 2', states: ['OuverteC2'] },
  { id: 'analyses', label: 'Analyses', states: ['AnalyseC2', 'Negociation'] },
  { id: 'publication', label: 'Publication Pulse', states: ['Publiee'] },
  { id: 'archivage', label: 'Archivage', states: ['Abandonnee'] },
];

/** Index de l'étape matérialisant l'état courant (0 = Création par défaut, jamais négatif). */
function currentStageIndex(state: CampaignStateDto): number {
  const index = CYCLE_STAGES.findIndex((stage) => stage.states.includes(state));
  return index < 0 ? 0 : index;
}

type StageStatus = 'done' | 'current' | 'upcoming';

/**
 * Statut d'une étape par rapport à l'étape courante. Cas nominal : amont = franchie, courante, aval = à venir.
 * Cas ABANDON (`isAbandoned`) : le parcours a été rompu — seule « Archivage » est courante, les autres étapes
 * restent « à venir » (jamais présentées comme franchies : honnêteté du visuel, on ne fabrique pas un parcours).
 */
function stageStatus(
  stageIndex: number,
  current: number,
  isAbandoned: boolean,
): StageStatus {
  if (isAbandoned) {
    return stageIndex === current ? 'current' : 'upcoming';
  }
  if (stageIndex < current) {
    return 'done';
  }
  if (stageIndex === current) {
    return 'current';
  }
  return 'upcoming';
}

/** Libellé TEXTE du statut (WCAG : porte l'information, jamais la couleur/forme seule). */
function statusLabel(status: StageStatus): string {
  switch (status) {
    case 'done':
      return 'étape franchie';
    case 'current':
      return 'étape en cours';
    case 'upcoming':
      return 'étape à venir';
  }
}

/** Classes du POINT d'étape selon le statut (renfort visuel ; le statut est porté par le texte sr-only). */
function dotClasses(status: StageStatus): string {
  switch (status) {
    // Franchie : point plein noir. Courante : point plein ambre. À venir : point vide (anneau neutre).
    case 'done':
      return 'border-foreground bg-foreground';
    case 'current':
      return 'border-dash-critical-fg bg-dash-critical-fg';
    case 'upcoming':
      return 'border-muted-foreground bg-transparent';
  }
}

export interface CampaignCycleNavigatorProps {
  /** État de campagne (autorité API) situé sur la frise. */
  readonly state: CampaignStateDto;
}

/**
 * Navigateur de cycle PERMANENT (DBU §2.5 l.1057-1059) — frise transverse de progression. Composant pur,
 * réutilisable sur toute page de la zone campagne. Aucune interaction (visuel d'orientation) : les
 * transitions restent portées par les sections d'action de l'écran de détail (autorité API).
 *
 * Restyling 2026-09-17 (maquette, spec restyling vue opérationnelle) : ligne COMPACTE logée à droite de la
 * barre haute de zone : 6 points (franchies pleines noires, courante ambre, à venir vides) puis le texte
 * « Étape n / 6 · <étape> en cours ». Les libellés d'étapes restent présents en sr-only (avec leur statut)
 * et en infobulle ; `aria-label` et `aria-current="step"` sont conservés.
 */
export function CampaignCycleNavigator({
  state,
}: CampaignCycleNavigatorProps): React.JSX.Element {
  const current = currentStageIndex(state);
  const isAbandoned = state === 'Abandonnee';
  const currentLabel = CYCLE_STAGES[current]?.label ?? '';

  return (
    <nav
      aria-label="Progression du cycle de la campagne"
      className="flex items-center gap-3"
    >
      <ol className="flex items-center gap-1.5">
        {CYCLE_STAGES.map((stage, index) => {
          const status = stageStatus(index, current, isAbandoned);
          return (
            <li
              key={stage.id}
              aria-current={status === 'current' ? 'step' : undefined}
              title={`${stage.label} : ${statusLabel(status)}`}
              className="flex items-center"
            >
              <span
                aria-hidden="true"
                className={`block size-2.5 rounded-full border ${dotClasses(status)}`}
              />
              <span className="sr-only">
                {stage.label}
                <span>, {statusLabel(status)}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <p aria-hidden="true" className="whitespace-nowrap text-sm text-muted-foreground">
        {`Étape ${String(current + 1)} / ${String(CYCLE_STAGES.length)} · ${currentLabel} en cours`}
      </p>
    </nav>
  );
}
